import { TEventKpiValueType } from '@shared/components/event-kpi-card/event-kpi-card.interface';
import { ICompareRange } from '@shared/components/profile-date-filter/profile-date-filter.interface';

export enum EEventAnalyticsTab {
  overview = 'overview',
  kpis = 'kpis',
  charts = 'charts',
  groups = 'groups',
  users = 'users',
}

/**
 * Mirrors the backend's `ApplicationPerformanceKpis` pydantic model, camelCased. Rate/conversion
 * fields are fractions in the 0..1 range (not already multiplied by 100) - `EventKpiCardComponent`
 * expects that and does the `* 100` itself when `valueType` is `'percentage'`. `avgTimeTo*`/
 * `avgTimeIn*`/`timeToFirst*` fields and `avgMcpCallDuration` are durations in milliseconds.
 */
export interface IApplicationPerformanceKpis {
  // Completion & adoption
  campaignCompletionRate: number;
  reportAdaptionConversion: number;
  briefingAdaptionConversion: number;
  campaignsWithReports: number;
  campaignsWithBriefings: number;
  briefingOpenRate: number;
  reportOpenRate: number;

  // Usage
  totalUsers: number;
  activeUsers: number;
  activeUsersFrontend: number;
  activeUsersMcp: number;
  activeUsersAvgDaily: number;
  totalUserGroups: number;
  activeUserGroups: number;
  campaignCreationsStarted: number;
  campaignCreationsFinished: number;
  reportsCreated: number;
  avgReportsPerCampaign: number;
  briefingsCreated: number;
  avgBriefingsPerCampaign: number;
  activeCampaigns: number;

  // Engagement
  avgTimeToCampaignCreationFinished: number;
  avgTimeInAnalyticsPopupOverview: number;
  avgTimeInAnalyticsPopupDeliverableView: number;
  avgViewsPerBriefing: number;
  avgViewsPerReport: number;
  timeToFirstCampaignCreationFinished: number;
  timeToFirstAnalyticsPopupOpened: number;
  timeToFirstBriefingCreated: number;
  timeToFirstReportPublished: number;
  timeToFirstMcpToolCall: number;
  campaignsWithAnalyticsViewed: number;
  campaignsWithAnalyticsViewedMcp: number;

  // MCP connector
  mcpAdoptionRate: number;
  mcpConnectedUsers: number;
  mcpSessions: number;
  mcpToolCalls: number;
  avgMcpToolCallsPerSession: number;
  avgMcpActiveDaysPerUser: number;
  mcpToolErrorRate: number;
  mcpToolRejectedRate: number;
  avgMcpCallDuration: number;

  // Costs
  /** Total AI costs incurred in the reporting window, in USD. */
  globalCosts: number;
  /** `globalCosts` / number of days in the reporting window, in USD. */
  avgDailyCosts: number;
  /** Average lifetime AI cost (in USD) of campaigns active during the reporting window. */
  avgCampaignCosts: number;
  /** Highest lifetime AI cost (in USD) among the same campaigns as `avgCampaignCosts`. */
  highestCampaignCosts: number;
}

export interface IApplicationPerformanceKpisPerUserGroup {
  userGroupId: string;
  userGroupName: string;
  kpis: IApplicationPerformanceKpis;
}

export interface IApplicationPerformanceKpisPerUser {
  userId: string;
  userName: string;
  userGroupId: string;
  userGroupName: string;
  kpis: IApplicationPerformanceKpis;
}

/** Query params shared by every event-analytics request - the page's full "Scope" selection. */
export interface IEventAnalyticsQueryParams {
  startDate: Date | null;
  endDate: Date | null;
  userGroupIds: string[];
}

/** The page-wide scope state driving every tab: the main range, an optional compare range, and the user group filter. */
export interface IEventAnalyticsScopeState {
  startDate: Date | null;
  endDate: Date | null;
  compareRange: ICompareRange | null;
  selectedUserGroupIds: Set<string>;
}

export enum EEventAnalyticsKpiSection {
  completionAdoption = 'completionAdoption',
  usage = 'usage',
  engagement = 'engagement',
  costs = 'costs',
  mcp = 'mcp',
}

/**
 * Declarative description of one KPI card: which raw field(s) it reads, how to format them, and
 * where it shows up. Driving the KPI cards off a config list (rather than hand-writing markup per
 * field) is what lets `EventKpiCardComponent` stay a single reusable component for both the
 * Overview tab's 4 highlighted cards and the KPIs tab's full breakdown.
 */
export interface IEventAnalyticsKpiCardConfig {
  /** Unique id, also used to build the `titleKey`/`infoKey` translation keys. */
  key: string;
  section: EEventAnalyticsKpiSection;
  valueType: TEventKpiValueType;
  /** Reads the card's headline value off a KPIs object (handles simple fields and derived ratios alike). */
  getValue: (kpis: IApplicationPerformanceKpis) => number | null;
  /** Reads the "/ total" value shown next to the headline (e.g. active users / total users). */
  getTotal?: (kpis: IApplicationPerformanceKpis) => number | null;
  enableProgressBar?: boolean;
  /** Colors a rise red and a drop green in the compare badge, for KPIs where a smaller value is the better outcome (error/rejection rates, time-to-first and response durations, AI costs). */
  lowerIsBetter?: boolean;
  /** Whether this card also appears among the Overview tab's highlighted cards. */
  showInOverview?: boolean;
  /** Hides this card from the KPIs tab's full breakdown (it may still show elsewhere via `showInOverview`). */
  hideInKpisTab?: boolean;
}

/**
 * A generic entity ranked by one numeric metric - powers the Groups/Users tabs' top-5 stacked
 * bar and circular graph visualizations, both of which rank whatever list they're given
 * client-side rather than relying on the backend to pre-sort/truncate it.
 */
export interface IRankedEntity {
  id: string;
  name: string;
  value: number;
}
